import { useEffect, useState, type ReactNode } from 'react';
import type { ClientPrincipal, Item } from '@mas-flow/types';
import { canPerform, type Capability } from '@mas-flow/domain';
import { AuthContext, filterCustomRoles, useAuth, type AuthState } from './useAuth';

interface MeResponse {
  clientPrincipal: ClientPrincipal | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    roles: [],
  });

  useEffect(() => {
    let cancelled = false;
    fetch('/.auth/me')
      .then((res) => {
        if (!res.ok) throw new Error(`/.auth/me ${res.status}`);
        return res.json() as Promise<MeResponse>;
      })
      .then((body) => {
        if (cancelled) return;
        if (!body.clientPrincipal) {
          setState({ status: 'anonymous', user: null, roles: [] });
          return;
        }
        setState({
          status: 'signed-in',
          user: body.clientPrincipal,
          roles: filterCustomRoles(body.clientPrincipal.userRoles),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', user: null, roles: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

/**
 * Mirror of the API's `canPerform` check. Returns true if the signed-in user
 * has the given capability. The optional `item` arg matters for state-
 * conditional capabilities; the implementation in `@mas-flow/domain` decides
 * how to use it.
 *
 * Client-side checks are UX only. The API is the source of truth.
 */
export function useCan(cap: Capability, item?: Pick<Item, 'lifecycle' | 'decision'>): boolean {
  const { roles } = useAuth();
  return canPerform({ roles }, cap, item).allowed;
}
